import React from 'react';
import { useLingui } from '@lingui/react/macro';

import type { BulkPrivacyChange } from './hooks/useGalleryManageMode';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { GalleryViewMode } from './ImageGallery.common';
import ImageGrid from './ImageGrid';
import Lightbox from './Lightbox';
import Overlay from './Overlay';

type ImageGalleryProps = {
  images: GalleryItem[] | undefined;
  galleryViewMode: GalleryViewMode;
  lightboxIndex: number;
  setLightboxIndex: React.Dispatch<React.SetStateAction<number>>;
  onClose: () => void;
  onImageClick: (index: number) => void;
  onViewGallery?: () => void;
  pagination?: PaginationConfig;
  renderImageFooter?: (index: number) => React.ReactNode;
  renderImageOverlay?: (index: number) => React.ReactNode;
  dimmedImageIds?: Set<number>;
  /**
   * Native only: have the grid scroll itself, so it windows its tiles and loads
   * further pages as the owner scrolls rather than all at once. Worth passing
   * for a gallery that can run to hundreds of photos. See
   * `ImageGridContainer.native`.
   */
  virtualizedGrid?: boolean;
  /** Whether these photos are the viewer's to curate. */
  isViewingOwnGallery?: boolean;
  onBulkPrivacyChange?: BulkPrivacyChange;
};

const ImageGallery = ({
  images,
  galleryViewMode,
  lightboxIndex,
  setLightboxIndex,
  onClose,
  onImageClick,
  onViewGallery,
  pagination,
  renderImageFooter,
  renderImageOverlay,
  dimmedImageIds,
  virtualizedGrid,
  isViewingOwnGallery,
  onBulkPrivacyChange,
}: ImageGalleryProps): JSX.Element => {
  const { t } = useLingui();
  if (galleryViewMode === GalleryViewMode.Grid) {
    return (
      <Overlay collapseMobilePx={false} onClose={onClose} ariaLabel={t`Image gallery`}>
        <ImageGrid
          images={images}
          onClose={onClose}
          onImageClick={onImageClick}
          pagination={pagination}
          renderImageOverlay={renderImageOverlay}
          virtualizedGrid={virtualizedGrid}
          dimmedImageIds={dimmedImageIds}
          isViewingOwnGallery={isViewingOwnGallery}
          onBulkPrivacyChange={onBulkPrivacyChange}
        />
      </Overlay>
    );
  }

  return (
    <Overlay collapseMobilePx onClose={onClose} ariaLabel={t`Image gallery`}>
      <Lightbox
        images={images}
        lightboxIndex={lightboxIndex}
        setLightboxIndex={setLightboxIndex}
        onClose={onClose}
        onViewGallery={onViewGallery}
        pagination={pagination}
        renderImageFooter={renderImageFooter}
        dimmedImageIds={dimmedImageIds}
      />
    </Overlay>
  );
};

export default ImageGallery;
